import React from 'react';
import { CheckCircle, Clock, Hourglass, Timer, XCircle } from '@phosphor-icons/react';
import { getTaskStatusColor, getTaskStatusLabel } from '../../../constants/taskColors.js';

const formatDateTime = (value) => {
  if (!value) {
    return 'Sin registrar';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Sin registrar';
  }

  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatDuration = (startValue, endValue) => {
  if (!startValue || !endValue) {
    return 'Sin registrar';
  }

  const startDate = new Date(startValue);
  const endDate = new Date(endValue);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 'Sin registrar';
  }

  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) {
    return 'Sin registrar';
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} min`);

  return parts.join(' ');
};

const STATUS_ORDER = ['pendiente', 'en_camino', 'iniciada', 'relevada', 'finalizada'];

const STATUS_ICON_MAP = {
  pendiente: Hourglass,
  en_camino: Timer,
  iniciada: Clock,
  relevada: Clock,
  finalizada: CheckCircle,
  cancelada: XCircle
};

const CIRCLE_RADIUS = 40;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

function TaskStatusSection({ task }) {
  const status = task?.status || 'pendiente';
  const endAt = task?.finishedAt || task?.cancelledAt;
  const currentIndex = STATUS_ORDER.indexOf(status);
  const progress = status === 'cancelada' || currentIndex === -1
    ? 0
    : ((currentIndex + 1) / STATUS_ORDER.length) * 100;
  const strokeDashoffset = CIRCLE_CIRCUMFERENCE - (progress / 100) * CIRCLE_CIRCUMFERENCE;
  const statusColor = getTaskStatusColor(status);
  const StatusIcon = STATUS_ICON_MAP[status] || Timer;

  return (
    <section className="rounded-3xl border border-line bg-surface/80 p-6 shadow-lg shadow-black/10">
      <div className="mb-5 flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor }} />
        <h2 className="text-lg font-semibold text-content">Estado de la tarea</h2>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="relative mx-auto w-fit shrink-0 lg:mx-0">
          <svg width="100" height="100" className="-rotate-90">
            <circle
              cx="50"
              cy="50"
              r={CIRCLE_RADIUS}
              fill="none"
              stroke="var(--app-border)"
              strokeWidth="6"
            />
            <circle
              cx="50"
              cy="50"
              r={CIRCLE_RADIUS}
              fill="none"
              stroke={statusColor}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={CIRCLE_CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-700"
            />
          </svg>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: `${statusColor}20` }}>
              <StatusIcon size={28} weight="bold" style={{ color: statusColor }} />
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="mb-4">
            <p className="mb-1 text-sm text-content-muted">Estado actual</p>
            <p className="text-xl font-bold" style={{ color: statusColor }}>
              {getTaskStatusLabel(status)}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-line bg-bg/35 px-3 py-2">
              <p className="text-xs text-content-muted">Inicio</p>
              <p className="text-sm font-medium text-content">{formatDateTime(task?.startedAt)}</p>
            </div>
            <div className="rounded-lg border border-line bg-bg/35 px-3 py-2">
              <p className="text-xs text-content-muted">Fin</p>
              <p className="text-sm font-medium text-content">{formatDateTime(endAt)}</p>
            </div>
            <div className="rounded-lg border border-line bg-bg/35 px-3 py-2">
              <p className="text-xs text-content-muted">Duración</p>
              <p className="text-sm font-medium text-content">{formatDuration(task?.startedAt, endAt)}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TaskStatusSection;
